import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, map, catchError, exhaustMap } from 'rxjs/operators';

import * as DaigakuActions from '../actions/daigaku.actions';
import { DaigakuService } from '../services/daigaku.service';
import { dispatchAppError } from '../actions/core.actions';
import { FirestoreError } from '../errors/firestore-error';
import { FirebaseCallableFunctionError } from '../errors/firebase-callable-function-error';
import { AppError } from '../errors/app-error';

@Injectable()
export class DaigakuEffects {
  private LOG_SOURCE = this.constructor.name;

  findDaigaku$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DaigakuActions.findDaigaku),
      switchMap(action =>
        this.daigakuService.findDaigaku(action.daigakuId).pipe(
          map(daigaku => DaigakuActions.findDaigakuSuccess({ daigaku })),
          catchError(e => {
            const error = e instanceof AppError ? e : FirestoreError.from(e);
            return of(DaigakuActions.findDaigakuFailure(), dispatchAppError({ source: this.LOG_SOURCE, error }));
          })
        )
      )
    )
  );

  findDaigakus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DaigakuActions.findDaigakus),
      switchMap(action =>
        this.daigakuService
          .findDaigakus(action.daigakuId, action.daigakuCode, action.daigakuName, action.daigakuSubName, action.prefectureId)
          .pipe(
            map(daigakus => DaigakuActions.findDaigakusSuccess({ daigakus })),
            catchError(e =>
              of(DaigakuActions.findDaigakusFailure(), dispatchAppError({ source: this.LOG_SOURCE, error: FirestoreError.from(e) }))
            )
          )
      )
    )
  );

  createDaigaku$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DaigakuActions.createDaigaku),
      exhaustMap(action =>
        this.daigakuService.createDaigaku(action.daigaku).pipe(
          map(daigaku => DaigakuActions.createDaigakuSuccess({ daigaku })),
          catchError(e => of(DaigakuActions.createDaigakuFailure({ error: FirebaseCallableFunctionError.from(e) })))
        )
      )
    )
  );

  updateDaigaku$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DaigakuActions.updateDaigaku),
      exhaustMap(action =>
        this.daigakuService.updateDaigaku(action.daigaku).pipe(
          map(daigaku => DaigakuActions.updateDaigakuSuccess({ daigaku })),
          catchError(e => of(DaigakuActions.updateDaigakuFailure({ error: FirebaseCallableFunctionError.from(e) })))
        )
      )
    )
  );

  deleteDaigaku$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DaigakuActions.deleteDaigaku),
      switchMap(action =>
        this.daigakuService.deleteDaigaku(action.id).pipe(
          map(response => DaigakuActions.deleteDaigakuSuccess({ response })),
          catchError(e => of(DaigakuActions.deleteDaigakuFailure({ error: FirebaseCallableFunctionError.from(e) })))
        )
      )
    )
  );

  constructor(private actions$: Actions, private daigakuService: DaigakuService) {}
}
